import moment from 'moment';
import S_guideline from '@/service/api/v1/guideline';
import S_guidelineAdmin from '@/service/api/v1/guidelineAdmin';
import S_guidelineArticleVersionAdmin from '@/service/api/v1/guidelineArticleVersionAdmin';
import H_time from '@/helpers/time';
import H_common from '@/helpers/common';
import H_systemClassMenu from '@/helpers/systemClassMenu';
import H_guidelineArticleVersionAdmin from '@/helpers/guidelineArticleVersionAdmin';
import S_factorySystemClass from '@/service/api/v1/factorySystemClass';

/** 合併 multiSystemClasses 樹狀 items，同 id 節點遞迴合併子項 */
function mergeMultiSystemClassTreeItems(accItems, newItems) {
  (newItems || []).forEach(newItem => {
    const existing = accItems.find(item => item.id === newItem.id);
    if (existing) {
      existing.items = mergeMultiSystemClassTreeItems(
        existing.items || [],
        newItem.items || [],
      );
    } else {
      accItems.push({
        id: newItem.id,
        items: mergeMultiSystemClassTreeItems([], newItem.items || []),
      });
    }
  });
  return accItems;
}

function filterClassItemsToLeafIds(classItems, leafIds) {
  const allowed = new Set((leafIds || []).map(id => String(id)));
  return (classItems || [])
    .map(classItem => {
      const leaves = (classItem.items || []).filter(leaf => allowed.has(String(leaf.id)));
      if (!leaves.length) return null;
      return { id: classItem.id, items: leaves };
    })
    .filter(Boolean);
}

function pruneClassItemsByRemovedLeaves(classItems, removedLeafIds) {
  const removed = new Set((removedLeafIds || []).map(id => String(id)));
  return (classItems || [])
    .map(classItem => {
      const leaves = (classItem.items || []).filter(leaf => !removed.has(String(leaf.id)));
      if (!leaves.length) return null;
      return { id: classItem.id, items: leaves };
    })
    .filter(Boolean);
}

/** 依 checked_at / 子任務完成數回傳狀態顯示用物件 */
function getCondition(checked_at, sub_tasks_count, sub_tasks_done_count) {
  const isChecked = !!checked_at;
  if (isChecked) {
    return { text: '已完成', color: 'gray' };
  }
  if (sub_tasks_count === sub_tasks_done_count) {
    return { text: '待審核', color: 'orange' };
  }
  return { text: '進行中', color: 'yellow' };
}

export default {
  setEventCardList(taskContent) {
    const eventCardList = [];
    if (!taskContent) return eventCardList;
    if (taskContent.article_version) {
      eventCardList.push({
        id: taskContent.article_version.id,
        type: 'article_version',
      });
    }
    if (taskContent.event) {
      eventCardList.push({
        id: taskContent.event.id,
        type: 'event',
      });
    }
    if (taskContent.alert) {
      eventCardList.push({
        id: taskContent.alert.id,
        type: 'alert',
      });
    }
    if (taskContent.audit_record) {
      eventCardList.push({
        id: taskContent.audit_record.id,
        type: 'auditRecord',
      });
    }
    if (taskContent.checklist_record) {
      eventCardList.push({
        id: taskContent.checklist_record.id,
        type: 'checklistRecord',
      });
    }
    if (
      taskContent.change_version &&
      taskContent.change_version.change
    ) {
      eventCardList.push({
        id: taskContent.change_version.change.id,
        type: 'change',
      });
    }
    if (taskContent.ll_broadcast) {
      eventCardList.push({
        id: taskContent.ll_broadcast.id,
        type: 'broadcast',
      });
    }
    if (taskContent.act_version) {
      eventCardList.push({
        id: taskContent.act_version.act.id,
        versionId: taskContent.act_version.id,
        type: 'actVersion',
      });
    }
    if (taskContent.internal_training_group) {
      eventCardList.push({
        id: taskContent.internal_training_group.id,
        type: 'internalTrainingGroup',
      });
    }
    if (taskContent.guideline_version) {
      const {
        id,
        guideline,
        name,
        announce_at,
        version_status,
      } = taskContent.guideline_version;
      const guidelineId = guideline.id;
      eventCardList.push({
        id: guidelineId,
        versionId: id,
        type: 'guidelineVersion',
        name,
        date: H_time.getDateDashFormat(announce_at),
        customClickEvent: async (t, from, fullPath, factoryId) => {
          if (version_status === 1) await S_guideline.show(guidelineId);
          else await S_guidelineAdmin.show(guidelineId);
          const query = {
            versionId: id,
            from,
            redirect: fullPath,
          };
          const path = version_status === 1
            ? `/factory/${factoryId}/guideline-content/${guideline.id}`
            : `/factory/${factoryId}/guideline-admin-content/${guideline.id}`;
          t.$router.push({
            path,
            query,
          });
        }
      });
    }
    if (taskContent.guideline_article_version) {
      const {
        id: articleVerId,
        guideline = {},
        guideline_version = {},
        name,
        announce_at,
        type,
        version_status,
      } = taskContent.guideline_article_version;
      const guidelineId = guideline ? guideline.id : null;
      const guidelineVerId = guideline_version ? guideline_version.id : null;
      eventCardList.push({
        id: guidelineId,
        versionId: articleVerId,
        type: 'guidelineArticleVersion',
        customQuery: (rawQuery) => ({
          ...rawQuery,
          versionId: guidelineVerId,
          articleVerId,
        }),
        name,
        date: H_time.getDateDashFormat(announce_at),
        customClickEvent: async (t, from, fullPath, factoryId) => {
          if (type === 'article') {
            if (version_status === 1) {
              await t.$refs.LvPopupInfoArticle.$_onOpenPopup(null, {
                articleVerId,
                guideline_article_version: taskContent.guideline_article_version,
              });
            } else {
              t.$refs.LvPopupInfoArticle.isLoading = true;
              const res = await S_guidelineArticleVersionAdmin.show(articleVerId, guidelineVerId);
              const data = H_guidelineArticleVersionAdmin.setShow(res);
              t.$refs.LvPopupInfoArticle.isLoading = false;
              await t.$refs.LvPopupInfoArticle.$_onOpenPopup(data);
            }
          } else {
            if (version_status === 1) await S_guideline.show(guidelineId);
            else await S_guidelineAdmin.show(guidelineId);
            const query = {
              versionId: guidelineVerId,
              from,
              redirect: fullPath,
              articleVerId,
            };
            const path = version_status === 1
              ? `/factory/${factoryId}/guideline-content/${guidelineId}`
              : `/factory/${factoryId}/guideline-admin-content/${guidelineId}`;
            t.$router.push({
              path,
              query,
            });
          }
        }
      });
    }
    return eventCardList;
  },
  isSubTaskSubmittable(subTask) {
    return subTask.reply && subTask.reply.trim().length;
  },
  setIndex(rawDataList, factorySystemClassRows = []) {
    const $_getDateOverDue = (date) => {
      if (!date) return 'red';
      const todayDate = H_time.getDateDashFormat(new Date());
      const targetDate = H_time.getDateDashFormat(date);
      return moment(todayDate).diff(moment(targetDate), 'days') > 0
        ? 'red'
        : 'gray';
    };
    return rawDataList.map(({
      id,
      created_at,
      checked_at,
      system_classes: systemClasses,
      system_subclasses,
      factory_system_classes,
      factory_system_subclasses,
      name,
      taker,
      expired_at,
      factory_tags,
      sub_tasks_count,
      sub_tasks_done_count,
    }) => {
      const condition = getCondition(checked_at, sub_tasks_count, sub_tasks_done_count);
      return {
        id,
        updateDate: H_time.getDateDashFormat(created_at, 'YYYY-MM-DD HH:mm:ss'),
        condition,
        systemClasses,
        multiSystemClasses: H_systemClassMenu.formatMultiSystemClassContentChips(
          {
            system_classes: systemClasses,
            system_subclasses,
            factory_system_classes,
            factory_system_subclasses,
          },
          { factorySystemClassRows },
        ),
        name: {
          text: name,
          chips: H_common.setFactoryTagsInDataTable(factory_tags),
        },
        toDoItem: `${sub_tasks_done_count}/${sub_tasks_count}`,
        taker,
        deadline: {
          icon: 'icon-duedate',
          color: $_getDateOverDue(expired_at),
          text: H_time.getDateDashFormat(expired_at),
        },
      };
    });
  },
  setCard(rawDataList) {
    if (!rawDataList || !rawDataList.length) return [];
    return rawDataList.map(({id, created_at, name: title, done_at, checked_at}) => {
      return {
        id,
        title,
        spec: [
          {
            title: '建立日期',
            text: H_time.getDateDashFormat(created_at),
          },
          {
            title: '任務狀態',
            text: !done_at && !checked_at
              ? '進行中'
              : done_at && !checked_at
              ? '待審核'
              : '已完成',
          },
        ],
      };
    });
  },
  setSubmitLinks(links, modulePageList) {
    if (!links || !Array.isArray(links)) return [];
    return links.map(link => {
      const url = link.url;
      const isUrlEsgoal = H_common.checkIsUrlEsgoal(url)
      const matchedModule = isUrlEsgoal ? H_common.getUrlModule(url, modulePageList) : null;
      return {
        ...link,
        type: isUrlEsgoal ? 'domain' : 'custom',
        url: isUrlEsgoal ? H_common.sliceUrlFromDomain(isUrlEsgoal.index, url) : url,
        from_module: matchedModule ? matchedModule.module : '',
        module_page_value: matchedModule ? matchedModule.value : '',
      };
    });
  },
  setShowLinks(links) {
    return links.map(({
      from_module,
      id,
      name,
      remark,
      url,
      module_page_value,
      record_links,
      type,
    }) => {
      return {
        id,
        name,
        remark,
        url,
        type,
        fromModule: from_module,
        modulePageValue: module_page_value,
        recordLinks: this.setShowRecordLinks(record_links),
      };
    });
  },
  setShowRecordLinks(links) {
    return links.map(({
      from_module,
      id,
      name,
      remark,
      url,
      module_page_value,
      type,
      created_at
    }) => {
      return {
        id,
        name,
        remark,
        url,
        type,
        fromModule: from_module,
        modulePageValue: module_page_value,
        createdAt: H_time.getDateDashFormat(created_at, 'YYYY-MM-DD HH:mm:ss'),
      };
    });
  },
  getLinks(type, subTaskIndex, taskContent) {
    if (type === 'task') return taskContent.links;
    else if (type === 'subTask') return taskContent.subTasks[subTaskIndex].links;
    return null;
  },
  addLink(type, subTaskIndex, taskContent) {
    const baseData = {
      type: 'domain',
      name: '',
      url: '',
      from_module: '',
      module_page_value: '',
      remark: '',
    };
    const list = this.getLinks(type, subTaskIndex, taskContent);
    if (list) list.push(baseData);
  },
  deleteLink(index, type, subTaskIndex, taskContent) {
    const list = this.getLinks(type, subTaskIndex, taskContent);
    if (list && list[index]) list.splice(index, 1);
  },
  inputLink($event, type, subTaskIndex, taskContent) {
    const {value, key, index} = $event;
    const list = this.getLinks(type, subTaskIndex, taskContent);
    if (list && list[index]) list[index][key] = value;
  },
  async pasteToLink(index, type, subTaskIndex, t, taskContentKey = 'taskContent') {
    try {
      const taskContent = t[taskContentKey];
      const list = this.getLinks(type, subTaskIndex, taskContent);
      if (!list || !list[index]) return;
      const rawData = await H_common.pasteText();
      let data;
      try {
        data = JSON.parse(rawData);
      } catch (e) {
        return;
      }
      if (typeof data !== 'object' || data === null) return;
      const linkData = {
        ...list[index],
        ...data,
        type: 'domain',
      };
      t.$set(list, index, linkData);
    } catch (error) {
      H_common.pasteFail();
    }
  },
  setBoardList(rawDataList, type) {
    if (!rawDataList || !rawDataList.length) return [];
    return type === 'draft'
      ? rawDataList.map(({id, updated_at, name}) => {
          return {
            id,
            updatedAt: H_time.getDateDashFormat(updated_at, 'YYYY-MM-DD HH:mm:ss'),
            name,
            rowItembutton: '繼續編輯',
          }
        })
      : rawDataList.map(task => {
          return {
            id: task.id,
            title: task.name ? task.name : task.title,
            totalNum: task.sub_tasks_count ? task.sub_tasks_count : task.totalNum,
            num: task.sub_tasks_done_count ? task.sub_tasks_done_count : task.num,
            expiredAt: task.expired_at ? task.expired_at : task.expiredAt,
          };
        });
  },
  setShowSubTasks(subTasks) {
    return subTasks.map(subTask => ({
      ...subTask,
      links: this.setShowLinks(subTask.links),
    }));
  },
  setSubtaskVisibilityText(value) {
    if (!value) return '';
    const textList = {
      system_subclass: '依待辦領域',
      taker: '限執行者',
      all: '所有待辦',
    };
    return textList[value] || '';
  },
  getMultiSystemClassesFromSubtasks(subTasks) {
    if (!subTasks?.length) return [];
    const classIds = ['factorySystemClass', 'systemClass'];
    const buckets = classIds.reduce((map, id) => {
      map[id] = [];
      return map;
    }, {});
    subTasks.forEach(subTask => {
      const multiSystemClasses = subTask.multiSystemClasses || [];
      classIds.forEach(classId => {
        const group = multiSystemClasses.find(cls => cls.id === classId);
        if (group?.items?.length) {
          mergeMultiSystemClassTreeItems(buckets[classId], group.items);
        }
      });
    });
    return classIds.reduce((acc, classId) => {
      if (buckets[classId].length) {
        acc.push({ id: classId, items: buckets[classId] });
      }
      return acc;
    }, []);
  },
  buildSystemClassTreeFromSubclasses(systemSubclasses, systemClasses) {
    if (!systemSubclasses?.length) return [];
    const subclassIds = systemSubclasses.map(item =>
      typeof item === 'object' ? item.id : item,
    );
    const classItems = [];
    (systemClasses || []).forEach(systemClass => {
      const leaves = (systemClass.system_subclasses || [])
        .filter(sub => subclassIds.includes(sub.id))
        .map(sub => ({ id: sub.id }));
      if (leaves.length) {
        classItems.push({ id: systemClass.id, items: leaves });
      }
    });
    if (!classItems.length) return [];
    return [{ id: 'systemClass', items: classItems }];
  },
  buildFactorySystemClassTreeFromSubclasses(factorySubclasses, factorySystemClasses) {
    if (!factorySubclasses?.length) return [];
    const subclassIds = factorySubclasses.map(item =>
      typeof item === 'object' ? item.id : item,
    );
    const classItems = [];
    (factorySystemClasses || []).forEach(factoryClass => {
      const leaves = (factoryClass.factory_system_classes || [])
        .filter(sub => subclassIds.includes(sub.id))
        .map(sub => ({ id: sub.id }));
      if (leaves.length) {
        classItems.push({ id: factoryClass.id, items: leaves });
      }
    });
    if (!classItems.length) return [];
    return [{ id: 'factorySystemClass', items: classItems }];
  },
  async buildModuleMultiSystemClasses(
    { system_subclasses = [], factory_system_subclasses = [] },
    systemClasses,
    factorySystemClasses,
  ) {
    const trees = [];
    const systemTree = this.buildSystemClassTreeFromSubclasses(
      system_subclasses,
      systemClasses,
    );
    if (systemTree.length) trees.push(...systemTree);
    if (factory_system_subclasses?.length) {
      const resolvedFactorySystemClasses = factorySystemClasses != null
        ? factorySystemClasses
        : await S_factorySystemClass.indexFactory({}, false);
      const factoryTree = this.buildFactorySystemClassTreeFromSubclasses(
        factory_system_subclasses,
        resolvedFactorySystemClasses,
      );
      if (factoryTree.length) trees.push(...factoryTree);
    }
    return trees;
  },
  mergeMultiSystemClasses(moduleMultiSystemClasses, subTasks) {
    const classIds = ['factorySystemClass', 'systemClass'];
    const buckets = classIds.reduce((map, id) => {
      map[id] = [];
      return map;
    }, {});
    const roots = [
      ...(moduleMultiSystemClasses || []),
      ...this.getMultiSystemClassesFromSubtasks(subTasks),
    ];
    roots.forEach(root => {
      if (root?.items?.length && buckets[root.id] !== undefined) {
        mergeMultiSystemClassTreeItems(buckets[root.id], root.items);
      }
    });
    return classIds.reduce((acc, classId) => {
      if (buckets[classId].length) {
        acc.push({ id: classId, items: buckets[classId] });
      }
      return acc;
    }, []);
  },
  removeSubclassLeavesFromMultiSystemClasses(
    roots,
    { system_subclasses = [], factory_system_subclasses = [] } = {},
  ) {
    const rootConfigs = [
      { rootId: 'systemClass', removed: system_subclasses },
      { rootId: 'factorySystemClass', removed: factory_system_subclasses },
    ];
    let next = (roots || []).map(root => ({
      id: root.id,
      items: [...(root.items || [])],
    }));
    rootConfigs.forEach(({ rootId, removed }) => {
      if (!removed?.length) return;
      const rootIndex = next.findIndex(root => root.id === rootId);
      if (rootIndex === -1) return;
      const prunedItems = pruneClassItemsByRemovedLeaves(next[rootIndex].items, removed);
      if (!prunedItems.length) {
        next = next.filter(root => root.id !== rootId);
        return;
      }
      next[rootIndex] = { id: rootId, items: prunedItems };
    });
    return next;
  },
  mergeAddedSubclassLeavesFromSubtasks(
    currentRoots,
    subTasks,
    { system_subclasses = [], factory_system_subclasses = [] } = {},
  ) {
    const classIds = ['factorySystemClass', 'systemClass'];
    const buckets = classIds.reduce((map, id) => {
      map[id] = [];
      return map;
    }, {});
    (currentRoots || []).forEach(root => {
      if (root?.items?.length && buckets[root.id] !== undefined) {
        mergeMultiSystemClassTreeItems(buckets[root.id], root.items);
      }
    });
    const addedByRoot = {
      systemClass: system_subclasses,
      factorySystemClass: factory_system_subclasses,
    };
    const subtaskTrees = this.getMultiSystemClassesFromSubtasks(subTasks);
    classIds.forEach(rootId => {
      const added = addedByRoot[rootId];
      if (!added?.length) return;
      const sourceRoot = subtaskTrees.find(root => root.id === rootId);
      if (!sourceRoot?.items?.length) return;
      const filteredItems = filterClassItemsToLeafIds(sourceRoot.items, added);
      if (filteredItems.length) {
        mergeMultiSystemClassTreeItems(buckets[rootId], filteredItems);
      }
    });
    return classIds.reduce((acc, classId) => {
      if (buckets[classId].length) {
        acc.push({ id: classId, items: buckets[classId] });
      }
      return acc;
    }, []);
  },
}
